import { sortTaskTableRows, sortTaskTemplateTableRows } from "./taskTableRowSort";
import { buildTeamMemberIdsMap, taskBelongsToTeam } from "./taskTeamMembership";

export const STATUS_FILTER_ALL = "all";
export const STATUS_FILTER_PENDING = "pending";
export const STATUS_FILTER_IN_PROGRESS = "in_progress";
export const STATUS_FILTER_CANCELLED = "cancelled";
export const STATUS_FILTER_COMPLETED = "completed";

/** Fixed top filter badges (independent of manage dialog). */
export const STATUS_FILTER_ORDER = [
  STATUS_FILTER_ALL,
  STATUS_FILTER_PENDING,
  STATUS_FILTER_IN_PROGRESS,
  STATUS_FILTER_CANCELLED,
  STATUS_FILTER_COMPLETED,
];

const BUILT_IN_FILTER_KEYS = new Set([
  STATUS_FILTER_PENDING,
  STATUS_FILTER_IN_PROGRESS,
  STATUS_FILTER_CANCELLED,
  STATUS_FILTER_COMPLETED,
]);

export const normalizeStatusKey = (status) =>
  String(status || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

const LEGACY_OVERDUE_KEYS = new Set(["over due", "overdue"]);
const PENDING_KEYS = new Set(["pending"]);
const IN_PROGRESS_KEYS = new Set(["in progress"]);
const CANCELLED_KEYS = new Set(["cancelled", "canceled"]);
const COMPLETED_KEYS = new Set(["complete", "completed"]);

/** Default on page load: Pending only. */
export const createDefaultStatusFilter = () => ({
  isAll: false,
  selected: new Set([STATUS_FILTER_PENDING]),
});

export const getStatusFilterBucket = (status) => {
  const key = normalizeStatusKey(status);
  if (LEGACY_OVERDUE_KEYS.has(key) || PENDING_KEYS.has(key)) return STATUS_FILTER_PENDING;
  if (IN_PROGRESS_KEYS.has(key)) return STATUS_FILTER_IN_PROGRESS;
  if (CANCELLED_KEYS.has(key)) return STATUS_FILTER_CANCELLED;
  if (COMPLETED_KEYS.has(key)) return STATUS_FILTER_COMPLETED;
  return "other";
};

export const rowMatchesStatusFilter = (status, filter) => {
  if (!filter || filter.isAll) return true;
  const selected = filter.selected || new Set();
  if (!selected.size) return true;
  const bucket = getStatusFilterBucket(status);
  if (bucket === "other") return false;
  return selected.has(bucket);
};

export const filterRowsByStatusFilter = (rows, filter) => {
  if (!Array.isArray(rows)) return [];
  if (!filter || filter.isAll) return rows;
  return rows.filter((row) => rowMatchesStatusFilter(row?.status, filter));
};

export const filterAndSortTaskRows = (rows, filter, options = {}) => {
  const filtered = filterRowsByStatusFilter(rows, filter);
  if (options.rowKind === "template") {
    return sortTaskTemplateTableRows(filtered);
  }
  return sortTaskTableRows(filtered);
};

/** Client-side list search (tasks / templates tables). */
export const taskListRowMatchesSearch = (row, rawQuery) => {
  const q = String(rawQuery || "").trim().toLowerCase();
  if (!q) return true;
  if (!row) return false;
  return (
    String(row.name || "").toLowerCase().includes(q) ||
    String(row.templateType || "").toLowerCase().includes(q) ||
    String(row.status || "").toLowerCase().includes(q) ||
    String(row.priority || "").toLowerCase().includes(q) ||
    String(row.createdBy || "").toLowerCase().includes(q) ||
    String(row.assigne || "").toLowerCase().includes(q) ||
    String(row.deadline || "").toLowerCase().includes(q)
  );
};

export const filterRowsBySearchQuery = (rows, rawQuery) => {
  if (!Array.isArray(rows)) return [];
  const q = String(rawQuery || "").trim();
  if (!q) return rows;
  return rows.filter((row) => taskListRowMatchesSearch(row, q));
};

export const filterGroupedTasksBySearch = (grouped, rawQuery) => {
  if (!grouped) return grouped;
  const q = String(rawQuery || "").trim();
  if (!q) return grouped;
  const mapRows = (list) => filterRowsBySearchQuery(list, q);
  return {
    ...grouped,
    leaderTasks: mapRows(grouped.leaderTasks || []),
    unassignedTasks: mapRows(grouped.unassignedTasks || []),
    members: (grouped.members || []).map((bucket) => ({
      ...bucket,
      tasks: mapRows(bucket.tasks || []),
    })),
  };
};

export const filterGroupedTasksByStatus = (grouped, filter) => {
  if (!grouped) return grouped;
  const mapRows = (list) => filterAndSortTaskRows(list, filter);
  return {
    ...grouped,
    leaderTasks: mapRows(grouped.leaderTasks || []),
    unassignedTasks: mapRows(grouped.unassignedTasks || []),
    members: (grouped.members || []).map((bucket) => ({
      ...bucket,
      tasks: mapRows(bucket.tasks || []),
    })),
  };
};

/**
 * Single-select status badge behavior:
 * - Click "All" → show every row (isAll).
 * - Click a status bucket → show ONLY that bucket.
 * - Re-click the same sole bucket → revert to default (Pending only).
 */
export const toggleStatusFilter = (filter, key) => {
  if (key === STATUS_FILTER_ALL) {
    return createAllStatusFilter();
  }

  if (!BUILT_IN_FILTER_KEYS.has(key)) {
    return filter || createDefaultStatusFilter();
  }

  const selected = filter?.selected instanceof Set ? filter.selected : new Set();
  const isAlreadySoleSelection =
    !filter?.isAll && selected.size === 1 && selected.has(key);

  if (isAlreadySoleSelection) {
    return createDefaultStatusFilter();
  }

  return {
    isAll: false,
    selected: new Set([key]),
  };
};

export const createAllStatusFilter = () => ({
  isAll: true,
  selected: new Set(),
});

export const isStatusFilterActive = (filter, key) => {
  if (key === STATUS_FILTER_ALL) return Boolean(filter?.isAll);
  if (filter?.isAll) return false;
  return (filter?.selected || new Set()).has(key);
};

const countTeamTasksByBucket = (tasks) => {
  const counts = {
    pending: 0,
    inProgress: 0,
    completed: 0,
    cancelled: 0,
    total: 0,
  };
  (tasks || []).forEach((task) => {
    counts.total += 1;
    const bucket = getStatusFilterBucket(task?.status);
    if (bucket === STATUS_FILTER_PENDING) counts.pending += 1;
    else if (bucket === STATUS_FILTER_IN_PROGRESS) counts.inProgress += 1;
    else if (bucket === STATUS_FILTER_CANCELLED) counts.cancelled += 1;
    else if (bucket === STATUS_FILTER_COMPLETED) counts.completed += 1;
  });
  return counts;
};

export const sortTeamsOverviewRows = (rows) =>
  [...(rows || [])].sort((a, b) => {
    const pendingDiff = (b.pending || 0) - (a.pending || 0);
    if (pendingDiff !== 0) return pendingDiff;
    const inProgressDiff = (b.inProgress || 0) - (a.inProgress || 0);
    if (inProgressDiff !== 0) return inProgressDiff;
    const cancelledDiff = (b.cancelled || 0) - (a.cancelled || 0);
    if (cancelledDiff !== 0) return cancelledDiff;
    const completedDiff = (b.completed || 0) - (a.completed || 0);
    if (completedDiff !== 0) return completedDiff;
    const totalDiff = (b.total || 0) - (a.total || 0);
    if (totalDiff !== 0) return totalDiff;
    return String(a.teamName || "").localeCompare(String(b.teamName || ""), undefined, {
      sensitivity: "base",
    });
  });

/** Teams overview status columns: Pending, In Progress, Completed, Cancelled. */
export const buildTeamsOverviewFromTaskRows = (teamsOverview, taskRows, settingsTeams = []) => {
  const teams = Array.isArray(teamsOverview) ? teamsOverview : [];
  const tasks = Array.isArray(taskRows) ? taskRows : [];
  const memberIdsByTeam = buildTeamMemberIdsMap(settingsTeams, teams);

  const rows = teams.map((team) => {
    const teamId = String(team.teamId || "");
    const memberIds = memberIdsByTeam.get(teamId) || new Set();
    const teamTasks = tasks.filter((task) => taskBelongsToTeam(task, teamId, memberIds));
    const counts = countTeamTasksByBucket(teamTasks);
    return {
      ...team,
      total: counts.total,
      pending: counts.pending,
      inProgress: counts.inProgress,
      completed: counts.completed,
      cancelled: counts.cancelled,
    };
  });

  return sortTeamsOverviewRows(rows);
};

/** @deprecated Use buildTeamsOverviewFromTaskRows — overview is not status-filtered. */
export const buildFilteredTeamsOverview = (teamsOverview, taskRows) =>
  buildTeamsOverviewFromTaskRows(teamsOverview, taskRows);
